import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dayIn } from '@delivery/shared';
import { useShowFuture } from '../lib/showFuture';
import { markSeen, seenSet } from '../lib/seen';
import { Pool } from './Pool';
import { RoutePlanBar } from '../components/RoutePlanBar';
import { CancelledList } from './CancelledList';
import { haptic, notify } from '../lib/native';
import { ApiFailure, api } from '../lib/api';
import { cachedGet } from '../lib/cache';
import { currentOrder } from '../lib/currentOrder';
import { outbox } from '../lib/outbox';
import { money } from '../lib/money';
import { STATUS_LABEL, type OrderStatus, type Task, type TasksResponse } from '../lib/types';
import { CurrentTask } from '../components/CurrentTask';
import { onAppResume } from '../lib/native';
import { PendingAcceptCard } from '../components/PendingAcceptCard';
import { CompleteSheet } from '../components/CompleteSheet';
import { CompletedList } from '../components/CompletedList';
import { TaskCardBody } from '../components/TaskCardBody';
import { PickupToggle, usePickupVisible } from '../components/PickupToggle';
import { searchTasks } from '../lib/task-search';
import { AlarmClock, CloudOff, Hourglass, Search, Store, X } from 'lucide-react';
import { InlineIcon } from '../components/InlineIcon';

/*
 * ★ 一次只**渲染**一屏多一点,滑到底自动接着渲染 —— 但**最终会显示完整**。
 *
 *   这里是渲染上限,不是数据上限:数据早就整批在手上了
 *   (`/driver/tasks` 一次返回全量,给离线用)。
 *   一千多行 DOM 在手机上是实打实的卡顿,所以要分批画;
 *   但**永远只画前 60 条**是另一回事 —— 那是「司机看不完自己的单」:
 *   他翻到底看到一句「还有 N 单没列出来」,而那 N 单他根本够不着。
 *   (用户原话:「你这样司机不就傻了吗」。)
 */
const PAGE = 40;

/** 还没走完的状态 —— 角标、「全部」、隔夜单提醒都按它数 */
const ACTIVE_STATUSES = ['ASSIGNED', 'ACCEPTED', 'PICKED_UP', 'IN_TRANSIT', 'FAILED'];

/*
 * ★ 期望送达那一天在今天之前(租户时区)。按**日期**比,不按时刻比。
 *
 *   用户定的口径:「非今日以及之后才送达的订单」。
 *   期望送达时刻一般存的是那一天的最后一刻,两种比法结果一样;
 *   但哪天有人把它写成某个钟点,按时刻比就会把今天下午的单算成「今天之前的」。
 *
 * ★ 没有期望送达日期的老单不算 —— 见下面 stale 那段。
 */
function isPastDay(tz: string, t: Task): boolean {
  if (!t.expectedDeliveryAt) return false;
  const d = new Date(t.expectedDeliveryAt);
  return Number.isFinite(d.getTime()) && dayIn(tz, d) < dayIn(tz);
}

/*
 * ★ 隔夜单弹窗**每次打开司机端只弹一次**,所以记在模块里,不记在组件里。
 *   打开订单详情时 <Today> 会整个卸载,记在 state 里的话,
 *   他每看完一单回来就再弹一次。刷新 / 重新打开页面才会再弹。
 */
let staleAsked = false;

/**
 * 今日页 —— 司机端的主界面。
 *
 * 三段结构(docs/14-driver-ux-spec.md §14.3):
 *   ① 待接单(仅在有时出现,橙色 + 倒计时)
 *   ② 当前任务(核心,一屏内看完并能直接操作)
 *   ③ 今日全部(折叠列表)
 *
 * ★「当前任务」优先于「任务列表」—— 直接告诉司机现在该做哪一件事,
 *   而不是给一个列表让他自己挑。
 */
export type ListTab =
  | 'pool'
  | 'all'
  /** 今天之前的、还没走完的单 —— 见下面 stale 那段 */
  | 'STALE'
  | 'SPECIAL'
  | 'ACCEPTED'
  | 'DELIVERING'
  | 'FAILED'
  | 'DONE'
  | 'CANCELLED';

export function Today({
  tab,
  onTabChange: setTab,
  specialItems: specialItemsOn,
  onOpenPickup,
  onOpenOrder,
  overlay,
  pendingIds,
  tz,
  assignWithoutAccept,
  canGrab,
  grabDisabledBy,
  dutyStatus,
  onDutyChanged,
  deliverOnly = false,
  confirmPrechecked = false,
  whatsapp = false,
}: {
  onOpenPickup: () => void;
  /** 当前分组 —— 由上层持有,详情页返回后才不会跳回「全部」 */
  tab: ListTab;
  onTabChange: (t: ListTab) => void;
  /** 租户开了「特殊物品」没有 —— 开了那一栏就一直在 */
  specialItems: boolean;
  /** 点一单进详情 —— 和地图进的是同一个 */
  onOpenOrder: (orderId: string) => void;
  /** 租户时区 —— 班次的「今天」按它算 */
  tz: string;
  /** 开了「派单免确认」:新单直接到「待取货」,没有确认这一步 */
  assignWithoutAccept: boolean;
  /** 有效值:租户开了抢单池 **且** 这个账号允许抢单 —— 决定「可领取」这一栏出不出现 */
  canGrab: boolean;
  grabDisabledBy: 'FEATURE' | 'POLICY' | null;
  /** 只显示「送达」—— 见 CompleteSheet 里的说明 */
  deliverOnly?: boolean;
  /** 代收、特殊物品这些确认项默认勾上 */
  confirmPrechecked?: boolean;
  /** 电话旁边显不显示 WhatsApp 按钮 */
  whatsapp?: boolean;
  dutyStatus: string;
  onDutyChanged: () => void;
  /** 乐观 UI:本地队列里的操作预期会把订单变成什么状态 */
  overlay: Map<string, OrderStatus>;
  /** 有待同步操作的订单,打「待同步」角标 */
  pendingIds: Set<string>;
}) {
  const [data, setData] = useState<TasksResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState<Task | null>(null);
  /* 发货那一段展不展开 —— 和「可领取」共用同一个开关(记在 localStorage) */
  const [showPickup, setShowPickup] = usePickupVisible();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [staleAt, setStaleAt] = useState<number | null>(null);
  /**
   * 列不列出「非当日达」的单。默认**不列**。
   *
   * ★ 这份状态**和地图那一屏共用**(见 lib/showFuture):
   *   在列表里勾上、切到地图,那边也该跟着只画今天的 ——
   *   一边藏一边画的话,他会照着地图规划一条把明天的活也算进去的路线。
   */
  /* 开关本身在顶栏(App.tsx,挨着上线按钮)—— 这一屏只读它 */
  const [showFuture] = useShowFuture();
  /**
   * ★ 司机手动指定的当前任务。
   *
   * 原来 current 完全由系统按状态优先级挑(配送中 > 待取货 > 其他),
   * 司机无法改。后果是**未送达的单永远够不着** —— 它排在最后,
   * 只要手上还有任何一单在配送中,那张卡就轮不到它,
   * 而重投/退回按钮只长在当前任务卡上。货就这么卡在车里。
   */
  /*
   * ★ 「我正在去的那一单」存在共享处,不是这一屏的局部状态。
   *   司机点导航的那一刻就等于宣布了他要去哪一单 —— 而那个动作
   *   发生在详情页里。存 localStorage 还有一层用处:
   *   他切到导航 App、回来时页面可能已经重载,而那正是他要找的东西。
   */
  const [pinnedId, setPinnedIdState] = useState<string | null>(() => currentOrder.get());
  const setPinnedId = useCallback((id: string | null) => {
    currentOrder.set(id);
    setPinnedIdState(id);
  }, []);
  useEffect(() => currentOrder.subscribe(setPinnedIdState), []);

  /*
   * ★ 顶部状态标签。
   *
   *   「可领取」是**所有还没人接的单**(订单池),其余几栏是
   *   **他自己的单**按状态过滤 —— 司机最常问的三个问题
   *   「有什么活可以接」「我该去取哪几单」「我手上还有几单在送」
   *   各对应一栏。
   *
   *   默认停在「全部」:一进来就该看到当前任务那张卡,
   *   而不是先做一次选择。
   */
  /** 当前这一栏已经画出来多少条 —— 滑到底就往下加 */
  const [visible, setVisible] = useState(PAGE);

  /**
   * 搜索关键词。
   *
   * ★ 不进 URL、不持久化。司机搜完就是为了点开那一单,
   *   回来时他要的是原来那一栏 —— 而不是半小时前打的半个名字
   *   还卡在框里把列表过滤着(那种「列表怎么少了一半」最难自己发现)。
   */
  const [q, setQ] = useState('');

  /*
   * ★ 当前分组由上层持有(见 App.tsx)。
   *   放在这里的话,打开订单详情 → <Today> 被卸载 → 返回时回到「全部」。
   */
  /* 见上面 props 里的说明 */

  /*
   * ★ 已经通知过的单,避免同一批反复弹。
   *   用 ref 不用 state:它只影响「要不要弹」,不影响渲染,
   *   放进 state 会让每次轮询都多一次无谓的重渲染。
   */
  const notified = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      /*
       * ★ 参数只在**勾上时**才加,不勾就什么都不传。
       *
       *   接口那头的默认已经反过来了:不传 = 只给今天(见 expected-window.ts)。
       *   这样一来,没刷新过的旧版司机端(它根本不知道有这个参数)
       *   拿到的自动就是收窄后的那一份 —— 不会再出现
       *   「同一个车队,有人看得到明天的单、有人看不到」。
       */
      const qs = showFuture ? '?includeFuture=1' : '';
      // 离线时回退到上一次的任务列表 —— 手上的活不能因为进了电梯就看不见
      /*
       * ★ 第二个参数是**离线时的退路**:缓存按完整路径存,勾上之后
       *   那条带参数的地址可能从来没被缓存过 —— 没有退路的话,
       *   司机在地下室拨一下这个框,整个列表会变成一句报错。
       */
      const r = await cachedGet<TasksResponse>(`/driver/tasks${qs}`, '/driver/tasks');

      /*
       * ★ app 在后台时来了新活,弹一条系统通知。
       *
       *   在这之前:司机把 app 切到后台、屏幕一黑,就**完全不知道来了新单**
       *   —— 服务端推送是空实现,他只能靠自己隔一会儿打开看一眼。
       *
       *   只在**页面不可见**时弹:在前台看着列表还弹通知是纯打扰,
       *   新单本来就直接出现在他眼前了。
       */
      if (document.hidden) {
        const fresh = r.data.tasks.filter(
          (t) =>
            (t.status === 'ASSIGNED' || t.status === 'ACCEPTED') && !notified.current.has(t.id),
        );
        if (fresh.length > 0) {
          void notify('有新任务', `${fresh.length} 单待处理,点开看看`);
          void haptic('heavy');
        }
      }
      // 不管在不在前台都记下来 —— 前台看见过的单,切到后台不该再弹一次
      for (const t of r.data.tasks) notified.current.add(t.id);

      setData(r.data);
      setStaleAt(r.stale ? r.cachedAt : null);
      setError(null);

      /**
       * ★ 提前把取货清单也缓存下来。
       *
       * 缓存只有被访问过才会有内容,而司机的真实动线是
       * 「在路上看今日 → 走进没信号的地下仓库 → 才点开取货」——
       * 等他点开时已经没网了,缓存还是空的。
       * 所以在**还有信号的时候**顺手预热,代价是一个请求。
       */
      if (r.data.tasks.some((t) => t.status === 'ACCEPTED')) {
        // 同一个口径预热 —— 缓存成另一份的话,进了地下室点开取货会对不上
        void cachedGet(`/driver/pickup-tasks${qs}`).catch(() => {});
        /*
         * ★ 不带参数的那两份也一并预热 —— 它们是离线退路指向的记录。
         *   只预热勾选状态下的那一份的话,**退路本身是空的**,
         *   而退路恰恰是在没信号时才用得上。
         */
        if (qs) {
          void cachedGet('/driver/tasks').catch(() => {});
          void cachedGet('/driver/pickup-tasks').catch(() => {});
        }
      }
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '加载失败');
    }
    /*
     * ★ `showFuture` 必须在依赖里,理由同下面那个轮询:
     *   少了它,勾选框一拨地址变了而 load 不会重建,
     *   拿到的还是上一份数据。
     */
  }, [showFuture]);

  /*
   * 池子里有几单 —— 只为了标签上那个数字。
   * ★ 单独轮询而不是塞进 /driver/tasks:那个接口是离线缓存的数据源,
   *   而池子是**别人也在抢**的东西,缓存下来只会让他看到已经没了的单。
   */
  const [poolCount, setPoolCount] = useState(0);
  useEffect(() => {
    if (!canGrab) return;
    const tick = () =>
      void api<{ data: unknown[]; total: number }>(
        `/driver/pool${showFuture ? '?includeFuture=1' : ''}`,
      )
        // ★ 角标显示的是**视野外也算在内的总数**,不是这次取回的那几条 ——
        //   截断之后角标写 50 而实际有 300,那个数字就骗人了
        .then((r) => setPoolCount(r.total))
        .catch(() => undefined);
    tick();
    const t = setInterval(tick, 30_000);
    return () => clearInterval(t);
    /*
     * ★ `showFuture` 必须在依赖里。少了它,勾选框一拨请求地址虽然变了,
     *   effect 却不会重跑 —— 角标停在旧数字上,要刷新整页才对
     *   (用户报的原话:「取消之后并没有立刻刷新数据」)。
     */
  }, [canGrab, showFuture]);
  /*
   * ★ 滑到底就多画一批。IntersectionObserver 比监听 scroll 省电,
   *   而这是一个整天开着的、装在手机上的页面。
   *
   * ★ 用**回调 ref** 绑,不是 useEffect + ref.current:
   *   底部那个哨兵要等数据到了、列表渲染出来才存在,
   *   而 effect 的依赖里没有「数据来了」这件事 —— 它跑的时候节点还是 null,
   *   于是观察器根本没绑上,滑到底什么也不会发生。
   *   (写用例时才发现:列表永远停在 40 条。)
   */
  const observerRef = useRef<IntersectionObserver | null>(null);
  const bottomCallbackRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setVisible((v) => v + PAGE);
      },
      { rootMargin: '300px' },
    );
    io.observe(node);
    observerRef.current = io;
  }, []);

  /**
   * 刚领到、但服务端那份列表里还没有的单。
   *
   * ★ 领取是「池子里少一单、我手上多一单」,而 outbox 那套乐观 overlay
   *   只能改**已经在列表里**的单的状态 —— 它变不出一条新的来。
   *   所以这里单独记一笔:计数当场加上,列表等 load 回来再补。
   *
   * ★ 不在本地伪造一条任务塞进列表:池子里的单是**脱敏**的
   *   (姓名打码、地址按级别给),照着它拼出来的那条会缺信息,
   *   而司机会拿它去干活。宁可那一行晚几百毫秒出现,也不给他一份残的。
   */
  const [justGrabbed, setJustGrabbed] = useState<string[]>([]);

  const onGrabbed = useCallback(
    (orderId: string) => {
      /*
       * ★ 领完**不跳走**。
       *   原来是 setTab('all') —— 司机想连着领三单,每领一单被弹走一次。
       *   用户原话:「方便用户快速领取多个订单」。
       */
      setJustGrabbed((ids) => (ids.includes(orderId) ? ids : [...ids, orderId]));
      void load();
    },
    [load],
  );

  useEffect(() => {
    void load();
    // 30 秒轮询兜底。有 WebSocket 后改为增量推送
    const t = setInterval(() => void load(), 30_000);
    return () => clearInterval(t);
  }, [load]);

  /**
   * 从后台回到前台时立刻刷一次。
   *
   * ★ 30 秒轮询在**前台**才跑。App 被切到后台之后系统会挂起计时器,
   *   司机吃完午饭回来打开 App,看到的是一小时前的任务列表 ——
   *   他会按着那份旧列表去跑,而那几单可能早就被改派了。
   *
   *   而且他不会怀疑 —— 界面看起来完全正常,只是数据是旧的。
   *   这正是「静默错误」最典型的形状。
   */
  useEffect(() => {
    let dispose: (() => void) | undefined;
    void onAppResume(() => void load()).then((d) => (dispose = d));
    return () => dispose?.();
  }, [load]);

  /**
   * ★ 队列排空后必须重新拉一次。
   *
   * 接单是「入队 + 乐观 overlay」,入队后立刻 load() 拿到的还是操作前的数据。
   * 等 outbox 同步成功,overlay 消失 —— 界面就退回那份旧数据,
   * 司机会看见刚接的单**又跳回「新任务」**,多半会再点一次接单。
   * 最长要等 30 秒轮询才自愈。E2E 里表现为「服务端已 ACCEPTED,界面还是 ASSIGNED」。
   */
  const prevPending = useRef(pendingIds);
  useEffect(() => {
    const drained = [...prevPending.current].some((id) => !pendingIds.has(id));
    prevPending.current = pendingIds;
    if (drained) void load();
  }, [pendingIds, load]);

  /**
   * 乐观 UI:本地队列里的操作立即反映到界面上,司机不用等网络。
   * 服务器数据回来后自然对齐 —— 队列项同步成功就从 overlay 里消失了。
   */
  const tasks = useMemo(
    () =>
      (data?.tasks ?? []).map((t) =>
        overlay.has(t.id) ? { ...t, status: overlay.get(t.id)! } : t,
      ),
    [data, overlay],
  );

  /*
   * ★ 打开司机端时,有今天之前没完成的单就先弹一个窗。
   *
   *   用户原话:「司机端页面打开后,如果还有之前的订单(非今日以及之后才送达订单)
   *   没有完成,则提示用户检查完成」。原来只有首页那条黄色横条,
   *   混在别的提示里容易被略过。横条保留:弹窗点掉之后,那几单还挂着就还有个入口。
   *
   * ★ 放在 effect 里而不是渲染时判断:要改模块里的 staleAsked,
   *   渲染时改的话,开发模式下的二次渲染会读到 true,弹窗就出不来。
   *   而且必须在下面 `if (!data) return` 之前 —— Hook 数量不能变。
   */
  const staleCount = tasks.filter(
    (t) => ACTIVE_STATUSES.includes(t.status) && isPastDay(tz, t),
  ).length;
  const [staleAsk, setStaleAsk] = useState(false);
  useEffect(() => {
    if (staleCount > 0 && !staleAsked) {
      staleAsked = true;
      setStaleAsk(true);
    }
  }, [staleCount]);

  /*
   * ★ 服务端那份里已经有了的,就从「刚领到」里摘掉 ——
   *   不摘的话计数会多算一遍(列表里一条、这里再加一)。
   */
  useEffect(() => {
    if (justGrabbed.length === 0) return;
    const arrived = new Set(tasks.map((t) => t.id));
    setJustGrabbed((ids) => {
      const left = ids.filter((id) => !arrived.has(id));
      return left.length === ids.length ? ids : left;
    });
  }, [tasks, justGrabbed.length]);

  const pendingAccept = useMemo(() => tasks.filter((t) => t.status === 'ASSIGNED'), [tasks]);

  /*
   * ★ 「刚派给我、我还没看见」的单。
   *
   *   开了「派单免确认」之后,新单直接落到 ACCEPTED,
   *   而待接单卡片筛的是 ASSIGNED —— 它不再出现,
   *   于是新活只是悄悄多一行,司机盯着手机也未必发现。
   *
   *   「看没看见」存在本机(见 lib/seen.ts):这是设备上的状态,
   *   不是订单的属性。
   */
  const [seen, setSeen] = useState(() => seenSet());
  const freshTasks = useMemo(
    () =>
      assignWithoutAccept ? tasks.filter((t) => t.status === 'ACCEPTED' && !seen.has(t.id)) : [],
    [assignWithoutAccept, tasks, seen],
  );
  const dismissFresh = useCallback(() => {
    const ids = freshTasks.map((t) => t.id);
    markSeen(ids);
    setSeen((prev) => new Set([...prev, ...ids]));
  }, [freshTasks]);

  const active = useMemo(
    () => tasks.filter((t) => ['ACCEPTED', 'PICKED_UP', 'IN_TRANSIT', 'FAILED'].includes(t.status)),
    [tasks],
  );

  /**
   * 「正在去的那一单」。
   *
   * ★ 司机点哪一单的导航就是哪一单(见 currentOrder),否则按状态优先级挑。
   *
   * ★ 但它必须落在**当前这一栏**里 —— 这张卡只在「配送中」和「未送达」
   *   出现,而司机切到「未送达」是为了处理那几单。这时候顶着一张
   *   写着配送中某单的卡片,只会让人以为切错了。
   */
  const current = useMemo(() => {
    const scope =
      tab === 'FAILED'
        ? active.filter((t) => t.status === 'FAILED')
        : tab === 'DELIVERING'
          ? active.filter((t) => ['PICKED_UP', 'IN_TRANSIT'].includes(t.status))
          : active;
    const pinned = pinnedId ? scope.find((t) => t.id === pinnedId) : null;
    return (
      pinned ??
      scope.find((t) => t.status === 'PICKED_UP' || t.status === 'IN_TRANSIT') ??
      scope.find((t) => t.status === 'ACCEPTED') ??
      scope[0] ??
      null
    );
  }, [active, pinnedId, tab]);

  // 选中的单离开进行中集合(送达/退回)后自动放开,不然会一直卡在一张空卡上
  useEffect(() => {
    if (pinnedId && !active.some((t) => t.id === pinnedId)) setPinnedId(null);
  }, [active, pinnedId]);

  const currentIndex = current ? active.findIndex((t) => t.id === current.id) + 1 : 0;

  /*
   * 多选批量 —— 用户要的:「像管理端那样多选后批量送达」。
   *
   * ★ 只在「配送中」这一栏开放。别的栏位里勾选没有意义:
   *   待取货要走取货那条(有分组和拍照),已完成的没什么可批的。
   *   在所有栏位都摆复选框,等于给他三个勾了没用的地方。
   */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  // 「东西都交到收件人手上了」/「这几单的钱都收齐了」—— 整批问一次,见下面 needsHandover / needsCash
  const [handoverOk, setHandoverOk] = useState(false);
  const [cashOk, setCashOk] = useState(false);
  const [batching, setBatching] = useState(false);
  const canPick = tab === 'DELIVERING';

  // 切栏 / 数据变了就清空 —— 留着上一栏勾的那几单,他会批到看不见的单上
  useEffect(() => {
    setPicked(new Set());
  }, [tab]);

  const togglePick = useCallback((id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /*
   * ★ 哪几单**批不了**,要提前说,不能让他点完在结果里看到一排失败。
   *
   *   · 要拍照的:一次批量交不出每一单的照片
   *   · 金额待定的代收:金额得逐单问商家,没法一次认下来
   *   这两类留给他一单一单进详情点 —— 那里才有拍照和填金额的入口。
   *
   * ★ 特殊物品和**金额已定**的代收不在这里挡掉,改成整批问一次。
   *
   *   用户原话:「如果有特殊物品和代收现金,只需要弹出提示一次就行了」。
   *   而且原来那版是错的:带特殊物品的单我既没挡也没带确认,
   *   服务端那道 specialItemsHandedOver 会逐单拒掉它们 ——
   *   他勾了十单,结果一排失败,而失败原因在批量结果里根本没地方显示。
   *
   * ★ 为什么代收在**司机端**可以一个勾,管理端不行:
   *   钱在他自己兜里,他低头数一遍就能对上那个合计;
   *   管理端点的人从没碰过那笔钱,替几十单认下到账是空口。
   */
  const pickedTasks = useMemo(() => tasks.filter((t) => picked.has(t.id)), [tasks, picked]);
  const blocked = useMemo(
    () => pickedTasks.filter((t) => t.proof?.delivery?.required === true || t.codPending === true),
    [pickedTasks],
  );
  const batchable = useMemo(
    () => pickedTasks.filter((t) => !blocked.some((b) => b.id === t.id)),
    [pickedTasks, blocked],
  );

  /** 要一起确认「东西交出去了」的那几单 */
  const specialPicked = useMemo(
    () => batchable.filter((t) => (t.specialItems ?? []).length > 0),
    [batchable],
  );
  /** 要一起确认「钱收齐了」的那几单(金额待定的不在内,它在 blocked 里) */
  const cashPicked = useMemo(
    () => batchable.filter((t) => Number(t.codAmount ?? 0) > 0),
    [batchable],
  );
  const cashTotal = useMemo(
    () => cashPicked.reduce((sum, t) => sum + Number(t.codAmount ?? 0), 0),
    [cashPicked],
  );
  /** 特殊物品按品类点个数 —— 「冻品 3 单、易碎 1 单」比「4 单带特殊物品」好核对 */
  const specialTally = useMemo(() => {
    const n = new Map<string, number>();
    for (const t of specialPicked) {
      for (const it of t.specialItems ?? []) n.set(it.label, (n.get(it.label) ?? 0) + 1);
    }
    return [...n.entries()].map(([label, count]) => `${label} ${count} 单`);
  }, [specialPicked]);

  const needsHandover = specialPicked.length > 0;
  const needsCash = cashPicked.length > 0;
  const canSubmit =
    batchable.length > 0 && (!needsHandover || handoverOk) && (!needsCash || cashOk);

  /*
   * ★ 勾选变了就把这两个确认**清掉**。
   *   不清的话:他为三单冻品勾过一次,再多勾两单别的特殊物品进来,
   *   那个勾还亮着 —— 于是新进来的两单是在他没看过的情况下被认下的。
   */
  useEffect(() => {
    setHandoverOk(false);
    setCashOk(false);
  }, [picked]);

  async function deliverPicked() {
    if (!canSubmit || batching) return;
    setBatching(true);
    try {
      /*
       * ★ 按「这一单有哪几样特殊物品 + 收多少钱」分组,每组发一次。
       *
       *   服务端两道门槛都是**逐单**核对的:
       *   specialItemsHandedOver 要求报上来的品类覆盖这一单的每一样,
       *   codCollectedIfNeeded 要求报上来的金额等于这一单该收的数。
       *   整批发同一个 payload 的话,只有恰好一致的那几单能过,
       *   其余的逐单被拒 —— 也就是我上一版的样子。
       *
       *   没有特殊物品、也不用收钱的单归在同一组(key 为 `|0`),
       *   就是原来那一次调用。
       */
      const groups = new Map<string, { codes: string[]; amount: number; ids: string[] }>();
      for (const t of batchable) {
        const codes = (t.specialItems ?? []).map((i) => i.code).sort();
        const amount = Number(t.codAmount ?? 0);
        const key = `${codes.join(',')}|${amount}`;
        const g = groups.get(key) ?? { codes, amount, ids: [] };
        g.ids.push(t.id);
        groups.set(key, g);
      }

      for (const g of groups.values()) {
        await outbox.enqueue({
          orderIds: g.ids,
          action: 'DELIVER',
          endpoint: '/orders/batch-transition',
          /*
           * ★ 按「本人签收」记,并且在按钮上写明白。
           *   批量没法逐单问签收方式,而默默按某一种记下来
           *   等于替他在每一单上做了一个他不知道的决定。
           */
          body: {
            action: 'DELIVER',
            orderIds: g.ids,
            batchKey: '',
            payload: {
              signedByType: 'SELF',
              ...(g.codes.length > 0 ? { specialItemsHandedOver: g.codes } : {}),
              // 现金:金额待定的单不在 batchable 里,所以这里的数一定是确定的
              ...(g.amount > 0 ? { codCollected: { amount: g.amount, method: 'CASH' } } : {}),
            },
          },
        });
      }
      setPicked(new Set());
      await load();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '批量送达失败');
    } finally {
      setBatching(false);
    }
  }

  async function acceptAll() {
    if (pendingAccept.length === 0) return;
    setBusyId('all');
    try {
      await outbox.enqueue({
        orderIds: pendingAccept.map((t) => t.id),
        action: 'ACCEPT',
        endpoint: '/orders/batch-transition',
        body: { action: 'ACCEPT', orderIds: pendingAccept.map((t) => t.id), batchKey: '' },
      });
      await load();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '接单失败');
    } finally {
      setBusyId(null);
    }
  }

  async function accept(task: Task) {
    setBusyId(task.id);
    try {
      await outbox.enqueue({
        orderIds: [task.id],
        action: 'ACCEPT',
        endpoint: `/orders/${task.id}/transitions`,
        body: { action: 'ACCEPT', idempotencyKey: '' },
      });
      await load();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '接单失败');
    } finally {
      setBusyId(null);
    }
  }

  if (!data) {
    return (
      <div className="min-h-dvh grid place-items-center text-slate-400">{error ?? '加载中…'}</div>
    );
  }

  /*
   * ★ `ACTIVE` 提到这儿来 —— 下面算角标就要用它。
   *   它是个常量,放哪儿都一样;而放在用它的代码**后面**时,
   *   TS 一个字都不会报(它只是块级作用域里的 const),
   *   浏览器控制台也看不到 —— 报的是 Vite 那边一行
   *   `Cannot access 'ACTIVE' before initialization`,而页面只是白屏。
   */
  const ACTIVE = ACTIVE_STATUSES;

  /*
   * ★ 「非当日达」这道筛子要在**算角标之前**定下来。
   *
   *   第一版它定义在下面、只用在列表上,于是角标写的是总数、
   *   点进去只有一部分 —— 用户报的原话:「上面的数量还是总数,
   *   除非刷新页面」。角标和列表不是一个口径的话,
   *   他只会以为丢单了,然后反复刷新。
   */
  const today = dayIn(tz);
  const isFutureDay = (t: Task): boolean =>
    t.expectedDeliveryAt ? dayIn(tz, new Date(t.expectedDeliveryAt)) > today : false;
  /*
   * ★ 这个数只能由**服务端**报。
   *
   *   不勾选时那些单压根没发过来,前端手上是空的 —— 自己数永远是 0,
   *   于是「还有 N 单不是今天送的」这句提示再也不会出现。
   *   而那句提示是司机唯一能知道「有东西被藏起来了」的线索:
   *   没有它,少掉的单在他看来就是**丢了**。
   *
   *   离线缓存里可能是老版本的响应(没有这个字段),那份数据是全量的,
   *   退回前端自己数即可。
   */
  const futureCount =
    data?.futureHidden ?? tasks.filter((t) => ACTIVE.includes(t.status) && isFutureDay(t)).length;
  const inScope = (t: Task): boolean => showFuture || !isFutureDay(t);
  /** 过了这道筛子的那些 —— 角标、统计、列表都用它,只有「隔夜」那一栏例外 */
  const inDay = tasks.filter(inScope);

  // counts 也按 overlay 重算 —— 否则乐观更新后「待取货 3 单」的角标还是旧数字
  const c = {
    doneToday: inDay.filter((t) => t.status === 'DELIVERED').length,
    /*
     * ★ 刚领到的算进「待取货」——  GRAB 一步到 ACCEPTED,
     *   而它们还没出现在服务端那份列表里。
     *   不加的话:司机连领三单,角标半天不动,他会以为没领上、再点一次。
     */
    toPickup: inDay.filter((t) => t.status === 'ACCEPTED').length + justGrabbed.length,
    toDeliver: inDay.filter((t) => ['PICKED_UP', 'IN_TRANSIT'].includes(t.status)).length,
    failed: inDay.filter((t) => t.status === 'FAILED').length,
  };

  /*
   * ★ 「全部」= 他**还没做完**的单,不是「今天碰过的所有单」。
   *
   *   原来它把已送达、已退回、**已取消**全都列进去 —— 而取消的单
   *   压根不需要他做任何事。开发库里有个司机手上 1108 条,
   *   其中 719 条是已取消:他要在这堆里找出今天还得送的那几单。
   *
   *   已取消单独一栏:它是「这单不用送了」的记录,查得到就够了,
   *   不该混在待办里。
   */

  /** 当前标签下要列出来的单 */
  /*
   * ★ 「特殊物品」这一栏:**只看自己手上的**。
   *
   *   `tasks` 本来就是这个司机自己的单(池子里那些在另一栏),
   *   所以这里只按「有没有特殊物品」筛。
   *
   * ★ 只列**还没结束**的:冻品要紧的是「现在车上这几箱」,
   *   而不是上周送掉的那些。已送达的翻「已完成」就行。
   */
  const specialTasks = inDay.filter(
    (t) => (t.specialItems ?? []).length > 0 && ACTIVE.includes(t.status),
  );

  /*
   * ★ 今天之前的、还没走完的单。
   *
   *   用户原话:「司机端登录之后,如果有今天之前的订单还没有完成,
   *   就提示司机注意检查订单的完成状态……有多少个没有完成,可以快速地查看」。
   *
   * ★ 为什么这是个真问题:进行中的单不限日期(后端 tasks 就是这么查的,
   *   昨天没送完的今天还得送)。于是它混在今天那几十单中间,
   *   长得和今天的一模一样 —— 而隔夜的那一单十有八九是
   *   **其实送到了、忘了点**。没人提醒的话,它会一直挂着,
   *   挂到对账时才被发现,那时候司机早忘了那天发生过什么。
   *
   * ★ 「今天」按**租户时区**算,不按手机时区:司机出个境、手机自动改了时区,
   *   零点就跟着偏几个小时,而单子还是照店里的日子在走。
   */
  /*
   * ★ 这里**不能用 useMemo**:上面有一句 `if (!data) return 加载中` ——
   *   在它后面加 Hook,第一次渲染(data 还是 null)和第二次的 Hook 数量就对不上,
   *   React 直接抛错,整个司机端首页白屏。
   *   而这两步只是对几十条数组做一次过滤,本来也不值得 memo。
   */
  /*
   * ★ 判据是**期望送达日期过了没有**,不是「这单什么时候到我手上的」。
   *
   *   用户原话:「司机端判断需不需要提醒的规则变成了判断当前日期
   *   是不是超过了期望送达的日期」。
   *
   *   以前是拿建单/派单/接单三个时间里最晚那个,去比昨天傍晚 6 点 ——
   *   推得对,但那是在客户端**每次渲染现推一遍**:同一单在调度屏上
   *   和司机手机上可能是两个答案,而答案还会随着「谁什么时候打开了 App」变。
   *   傍晚 6 点那条规则现在挪到了下单那一刻(见 shared 的 defaultExpectedDay),
   *   算一次写进数据,两边看到的是同一个日子。
   *
   *   这样还多了一件以前做不到的事:调度可以**明着**把某几单改到明天
   *   (批量改期望送达日期),而不是靠司机自己忽略提醒。
   *
   * ★ 判断用的是模块顶上的 isPastDay(按日期比,不按时刻比)。
   */

  /*
   * ★ 没有期望送达日期的老单**不提醒**。
   *
   *   这个字段是后来才有的,库里之前的单全是空的。把「空」当成
   *   「早就该送了」的话,上线那天每个司机都会收到一条几十单的提醒 ——
   *   而那些单多半早就处理完了,他只会学会忽略这条横条。
   */
  const stale = tasks.filter((t) => ACTIVE.includes(t.status) && isPastDay(tz, t));

  /*
   * ★ 期望送达日期在**今天之后**的单,默认不列出来。
   *
   *   用户原话:「司机端显示列表的时候增加一个 checkbox 显示非当日达订单,
   *   默认不勾选,勾选后才显示非今天的订单」。
   *
   *   傍晚之后录的第二天的单,现在会明明白白落在明天(见 defaultExpectedDay)。
   *   混在今天的队列里的话,司机点货时会把明天的也翻出来找 ——
   *   而它们长得和今天的一模一样。
   *
   * ★ 只影响**这张列表**,不影响:
   *     · 「待接单」那条提示 —— 派下来的单有接单超时,藏起来会让它
   *       悄悄退回派单池,而司机根本没见过它
   *     · 顶上的「今日战况」—— 那是一整天的汇总,不是待办队列
   *   藏错地方的代价比多显示几单大得多。
   *
   * ★ 过期的单不受影响:它们的日期在今天**之前**,本来就不是「非当日达」。
   */
  const byTab =
    tab === 'STALE'
      ? stale
      : tab === 'SPECIAL'
        ? specialTasks
        : tab === 'ACCEPTED'
          ? inDay.filter((t) => t.status === 'ACCEPTED')
          : tab === 'DELIVERING'
            ? inDay.filter((t) => ['PICKED_UP', 'IN_TRANSIT'].includes(t.status))
            : tab === 'FAILED'
              ? inDay.filter((t) => t.status === 'FAILED')
              : tab === 'DONE'
                ? inDay.filter((t) => ['DELIVERED', 'RETURNED'].includes(t.status))
                : inDay.filter((t) => ACTIVE.includes(t.status));

  /*
   * ★ 一打字就**不分栏**了,按手上全部的单搜(含已完成的)。
   *
   *   只在当前这一栏里搜会有这种事:他在「配送中」搜「王」,
   *   而那一单还停在「待取货」—— 屏幕上写「没找到」,
   *   而他明明知道这单在手上。于是他会以为单丢了,然后去打电话问调度。
   *   结果里每张卡片本来就写着状态,所以不分栏不会造成误会。
   *
   * ★ 用 `tasks` 而不是 `inDay`:被「非当日达」折叠掉的单也要搜得到 ——
   *   他要找的恰恰常常是那种「不知道哪天的那一单」。
   */
  const searching = q.trim() !== '';
  /*
   * ★ 搜索**只搜当前这一栏**(用户定的:「搜索结果就是从当前选择的标签里面搜」)。
   *
   *   代价是他可能站在「配送中」搜一个还停在「待取货」的单 ——
   *   所以下面那条提示不能省:零结果时要说出**那一单在哪一栏**,
   *   并且一点就跳过去。不说的话他会以为单丢了,然后打电话问调度。
   */
  const listed = searching ? searchTasks(byTab, q) : byTab;

  /**
   * 当前这一栏没搜到时,别处搜到的第一单在哪一栏。
   *
   * ★ 只在「这一栏零结果」时算 —— 有结果时再提示别处还有,
   *   是在他已经找到东西之后往屏幕上加噪音。
   */
  const elsewhere = (() => {
    if (!searching || listed.length > 0) return null;
    const hits = searchTasks(tasks, q);
    if (hits.length === 0) return null;
    const done = hits.filter((t) => ['DELIVERED', 'RETURNED'].includes(t.status));
    // 还没做完的优先 —— 他要找的通常是还要送的那单
    const live = hits.filter((t) => ACTIVE.includes(t.status));
    if (live.length > 0) return { key: 'all' as const, label: '全部', n: live.length };
    if (done.length > 0) return { key: 'DONE' as const, label: '已完成', n: done.length };
    return null;
  })();

  /*
   * ★ 终态那两栏要**倒着排**。
   *
   *   服务端按创建时间正序返回(待办那几栏正需要这样:先来的先送)。
   *   但「已完成」「已取消」是拿来**查**的 —— 司机想知道的是
   *   「刚才那单是不是取消了」,而不是三周前的第一单。
   *   正序 + 只显示前 60 条 = 他要找的那单永远不在里面。
   */
  const ordered = tab === 'DONE' ? [...listed].reverse() : listed;
  const shown = ordered.slice(0, visible);
  const hidden = ordered.length - shown.length;

  const TABS: Array<{ key: typeof tab; label: string; n: number }> = [
    /*
     * ★ 「可领取」也是常驻的。
     *
     *   原来不能抢单时整栏消失 —— 而司机记得昨天还有这一栏,
     *   今天不见了。**消失的入口和坏掉的功能长得一模一样**,
     *   他没法从界面上得到任何解释(这个问题被问了两次)。
     *   现在留着,点进去说明是哪一边关的:是本店没开抢单池,
     *   还是他这个账号的策略不允许 —— 两种要找的人不一样。
     */
    { key: 'pool' as const, label: '可领取', n: canGrab ? poolCount : 0 },
    /* ★ 角标要和列表同一个口径 —— 角标写 12、点进去只有 8 条,他会以为丢单了 */
    { key: 'all', label: '全部', n: inDay.filter((t) => ACTIVE.includes(t.status)).length },
    { key: 'ACCEPTED', label: '待取货', n: c.toPickup },
    { key: 'DELIVERING', label: '配送中', n: c.toDeliver },
    /*
     * ★ 「未送达」得有自己的一栏。
     *
     *   在这之前它一个标签都不属于,只能在「全部」里翻 —— 而这些单
     *   **货还在司机手上**,是最需要处理的一类(重投还是退回)。
     *   一单都没有的时候不占位置:显示一个点进去必然是空的入口,
     *   比不显示更糟。
     */
    ...(c.failed > 0 ? [{ key: 'FAILED' as const, label: '未送达', n: c.failed }] : []),
    /*
     * ★ 「隔夜单」只在真有的时候出现 —— 它不是一个常规分类,
     *   是一句「这几单不对劲,去看一眼」。一单都没有的时候留着这一栏,
     *   人会慢慢把它当背景,真出现的那天也不会点。
     */
    ...(stale.length > 0 ? [{ key: 'STALE' as const, label: '隔夜单', n: stale.length }] : []),
    /*
     * ★ 「特殊物品」——冻品、活鲜、易碎这些。
     *
     *   司机手上一天几十单,而这几单是**有时限的**:冻品在车里放两小时就废了。
     *   混在「全部」里翻的话,他得一条条看有没有那个标记。
     *
     * ★ 位置排在「已完成」前面 —— 待办那几栏(可领取/全部/待取货/配送中/未送达)
     *   是司机一天里反复扫的,中间插一栏会把他熟悉的顺序打乱;
     *   而这一栏是「今天有没有要特别当心的货」,看一眼就够,
     *   和「已完成」「已取消」这些查得到的东西挨着更合适。
     *
     * ★ 开了这个功能就**一直显示**,哪怕现在是 0 单。
     *
     *   原来是「有单才显示」——而一个会消失的标签,人只会以为功能没做
     *   (用户原话:「怎么看不到」)。「我车上现在没有需要特别注意的货」
     *   和「这套系统不支持特殊物品」是两件完全不同的事,
     *   而空标签正是用来回答前者的。
     */
    ...(specialItemsOn
      ? [{ key: 'SPECIAL' as const, label: '特殊物品', n: specialTasks.length }]
      : []),
    { key: 'DONE', label: '已完成', n: c.doneToday },
    /*
     * ★ 「已取消」是常驻的一栏,不像「未送达」那样有才显示。
     *
     *   它不是待办,是**查得到的历史**:客户第二天打电话来问
     *   「昨天那单怎么没到」,司机要能当场回答「那单取消了」。
     *   按「今天有没有取消单」来决定这一栏在不在的话,他昨天见过的入口
     *   今天就不见了 —— 而消失的入口和坏掉的功能看起来一模一样。
     *
     *   数字不放在这里:这一栏走的是分页接口(见 CancelledList),
     *   总数在里面显示,而标签上挂一个「今天的取消数」会让人以为
     *   点进去就只有那几条。
     */
    { key: 'CANCELLED' as const, label: '已取消', n: 0 },
  ];

  return (
    <div className="pb-24">
      {/*
        ★ 智能推荐路线。放在列表上方、标签栏之前 ——
          司机出车前的第一个动作就是它,藏在某一栏里面的话他找不到。
      */}
      <RoutePlanBar
        /*
         * ★ 这个数**不能**只数列表里的单 —— 必须把「非当日达」藏掉的那些加回来。
         *
         *   司机端默认不显示未来日期的单,而那一档是**服务端**就筛掉的
         *   (`/driver/tasks` 不带 includeFuture 时根本不返回它们),
         *   所以在前端数 `tasks` 也数不到。于是一个手上全是隔夜单的司机
         *   count 是 0,按钮整个不渲染 —— 而他恰恰单最多、最需要排顺序。
         *   (用户报的「配送中我也没有看到生成路径优化这个功能」就是这个。)
         *
         *   路线规划本身不看日期(见 route.service 的 status 筛选),
         *   所以这里的口径要和它一致:他手上还没送掉的单,有几单。
         *
         * ★ `futureHidden` 在勾了「非当日达」时是 0(那时单都在 tasks 里),
         *   所以这两项不会重复计。
         *
         * ★ 它含「待接单」,而规划不排那些 —— 所以只有待接单时按钮会出现、
         *   点下去回「手上没有要送的单」。那句话是真话、也说得清,
         *   比按钮时有时无好:消失的入口和坏掉的功能长得一模一样。
         */
        count={tasks.filter((t) => ACTIVE.includes(t.status)).length + (data?.futureHidden ?? 0)}
        route={data?.route ?? null}
        onPlanned={load}
      />

      {/*
        ★ 班次条**暂时不挂**。
 
          用户定的:「司机端那边暂时不需要显示什么已排班」。
          现在不打卡,排班对司机来说只有一个后果 ——
          没排班的那天「可领取」是空的。而那一条横条能告诉他的
          只有「今天 09:00–19:00」,他照着它什么都不用做,
          也什么都做不了:纯占屏幕。
 
          组件本身留着(`components/ShiftBar.tsx`),`/shifts/mine` 也留着 ——
          以后要恢复打卡或者要显示班次的话,在这里挂回来就行。
      */}

      {/*
        ★ 隔夜单的提醒。一登录就看得见,而不是等他自己去翻。

          ★ 横条和上面的弹窗都要有:弹窗每次打开只弹一次、点掉之后那几单还在 ——
            人很容易学会闭眼点「稍后」。这条横条**只要那几单还挂着就一直在**,
            处理完自己消失,所以它永远不会变成背景。

          ★ 话要说清是哪一天的、共几单,并且给一个直接能点进去的入口 ——
            光说「有未完成的订单」等于让他自己去几十单里找。
      */}
      {staleAsk && stale.length > 0 && (
        <div className="fixed inset-0 z-[55] flex items-end bg-black/60">
          <div
            className="w-full rounded-t-2xl bg-white dark:bg-slate-800 p-4 pb-safe"
            role="alertdialog"
            aria-modal="true"
            aria-label="今天之前的订单没完成"
          >
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              有 {stale.length} 单是今天之前的,还没完成
            </h3>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              请逐单核对:已经送到的补一下「送达」,送不了的标「失败」或退回。
            </p>
            {/*
              ★ 说清不点送达的后果,司机才有动力补。
                用户原话:「要提示及时点送达,送达后第二天才会出现在结算单中」。
            */}
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900
                          dark:bg-amber-950/40 dark:text-amber-200">
              送到后请及时点「送达」:点了送达的单,第二天才会出现在结算单里。
            </p>
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => setStaleAsk(false)}
                className="flex-1 rounded-xl border border-slate-300 dark:border-slate-600 py-3
                           text-slate-700 dark:text-slate-200"
              >
                稍后
              </button>
              <button
                onClick={() => {
                  setStaleAsk(false);
                  setTab('STALE');
                  setVisible(PAGE);
                }}
                className="flex-[2] rounded-xl bg-brand-600 py-3 font-medium text-white"
              >
                去检查
              </button>
            </div>
          </div>
        </div>
      )}

      {stale.length > 0 && tab !== 'STALE' && (
        <button
          aria-label="隔夜单提醒"
          onClick={() => {
            setTab('STALE');
            setVisible(PAGE);
          }}
          className="mx-4 mt-3 flex w-[calc(100%-2rem)] items-center gap-3 rounded-xl border
                     border-amber-300 bg-amber-50 px-3 py-2.5 text-left
                     dark:border-amber-800 dark:bg-amber-950/40"
        >
          <InlineIcon icon={AlarmClock} className="text-xl text-amber-600" />
          <span className="flex-1">
            <span className="block text-sm font-medium text-amber-900 dark:text-amber-200">
              有 {stale.length} 单是今天之前的,还没走完
            </span>
            <span className="block text-xs text-amber-800/80 dark:text-amber-300/80">
              核对一下是不是已经送到了 —— 送到了就补一下状态
            </span>
          </span>
          <span className="shrink-0 rounded-lg bg-amber-600 px-2.5 py-1 text-xs font-medium text-white">
            去看看
          </span>
        </button>
      )}

      {/*
        ★ 搜索框**不属于任何一栏** —— 自己一行,摆在标签栏**上面**,每一栏都有。

          用户原话:「搜索应该是每个标签上面都可以搜的,所以你可以把它独立到
          标签之外」、「放到可领取全部这一行的上面」。常驻的理由:他站在楼下要核对「刚才那个姓王的是不是这一单」,
          那一刻不会去翻菜单。代价是一行高度,值得。

        ★ 「可领取」和「已取消」那两栏也有,但走各自的数据源(它们不在
          `/driver/tasks` 那份快照里):可领取在本地过滤那一页,
          已取消把关键词交给服务端 —— 那一栏是分页的,只过滤已经滑出来的
          几页等于「搜不到更早的」,而他要查的恰恰是更早的单。
      */}
      <div className="mt-2 px-4">
        <div className="relative">
          <input
            type="search"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              // 换了关键词就从头画 —— 否则接着上一次的 visible,前面几十条是空的
              setVisible(PAGE);
            }}
            placeholder="在这一栏里搜:单号、收件人、电话、地址"
            aria-label="搜索我的订单"
            className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-9 text-sm
                       text-slate-900 placeholder:text-slate-400
                       dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <InlineIcon icon={Search} />
          </span>
          {searching && (
            <button
              onClick={() => {
                setQ('');
                setVisible(PAGE);
              }}
              aria-label="清空搜索"
              className="absolute right-2 top-1/2 -translate-y-1/2 px-2 text-slate-400"
            >
              <InlineIcon icon={X} />
            </button>
          )}
        </div>

        {/*
          ★ 零结果时**必须说出那一单在哪一栏**,而不是只说「没找到」。

            搜索只搜当前这一栏,所以「没找到」有两种完全不同的意思:
            真的没有这个人,和「他在另一栏」。只说前一种的话,
            司机会以为单丢了 —— 然后打电话问调度(而他正在开车)。
        */}
        {searching && tab !== 'pool' && tab !== 'CANCELLED' && (
          <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
            {listed.length > 0 ? (
              `这一栏里找到 ${listed.length} 单`
            ) : elsewhere ? (
              <>
                这一栏没有 ——
                <button
                  onClick={() => {
                    setTab(elsewhere.key);
                    setVisible(PAGE);
                  }}
                  className="ml-1 font-medium text-brand-600 underline dark:text-brand-500"
                >
                  「{elsewhere.label}」里有 {elsewhere.n} 单
                </button>
              </>
            ) : (
              '没找到 —— 换个关键词试试,电话只记得后四位也能搜'
            )}
          </p>
        )}
      </div>

      {/*
        ★ 标签栏一直在,不随内容消失 —— 司机要能随时切回去看池子。
          「可领取」只在真的能领时出现(见 driver.service 里 canGrab 的说明):
          显示一个点进去必然是空的入口,比不显示更糟。
      */}
      <div
        role="tablist"
        aria-label="任务状态"
        className="mt-3 flex gap-2 overflow-x-auto px-4 pb-1"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => {
              setTab(t.key);
              // 换一栏就从头开始画 —— 否则新栏一进来就是几百行
              setVisible(PAGE);
            }}
            className={`shrink-0 rounded-full px-3 py-1.5 text-sm transition ${
              tab === t.key
                ? 'bg-brand-600 text-white'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
            }`}
          >
            {t.label}
            {t.n > 0 && <span className="ml-1 tabular-nums">{t.n}</span>}
          </button>
        ))}
      </div>

      {/*
        ★ 搜索框放在标签栏**下面**,而且常驻。

          放进某个菜单里的话它等于不存在 —— 司机站在楼下要核对
          「刚才那个姓王的是不是这一单」,那一刻他不会去翻菜单。
          代价是一行高度,值得。

        ★ 「可领取」和「已取消」两栏不显示它:那两栏的数据不在这份
          任务列表里(各自单独取),在这儿搜等于搜不到自己眼前的东西 ——
          一个搜不动的搜索框比没有搜索框更让人困惑。
      */}
      {tab === 'pool' && (
        <Pool
          keyword={q}
          grabDisabledBy={grabDisabledBy}
          dutyStatus={dutyStatus}
          onGrabbed={onGrabbed}
          onDutyChanged={onDutyChanged}
          onOpenOrder={onOpenOrder}
        />
      )}
      {tab === 'CANCELLED' && <CancelledList keyword={q} onOpenOrder={onOpenOrder} />}

      {tab !== 'pool' && tab !== 'CANCELLED' && (
        <>
          {error && (
            <div
              className="mx-4 mt-3 rounded-xl bg-red-50 dark:bg-red-950 border border-red-200
                        dark:border-red-900 px-4 py-3 text-red-700 dark:text-red-300 text-sm"
            >
              {error}
              <button onClick={() => setError(null)} aria-label="关闭" className="float-right font-medium">
                <InlineIcon icon={X} />
              </button>
            </div>
          )}

          {/* ★ 顶部的「离线模式」只说明网络状态,这里说明的是**数据有多旧** ——
             司机要据此判断能不能相信屏幕上的单量 */}
          {staleAt != null && (
            <p
              className="mx-4 mt-3 rounded-xl bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-sm
                      text-amber-800 dark:text-amber-300"
            >
              <InlineIcon icon={CloudOff} /> 离线数据 · 更新于{' '}
              {new Date(staleAt).toLocaleTimeString('zh-CN', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          )}

          {/* ① 待接单 —— 最高优先级,橙色 + 呼吸动画 + 倒计时 */}
          {pendingAccept.length > 0 && (
            <PendingAcceptCard
              tasks={pendingAccept}
              busy={busyId === 'all'}
              onAcceptAll={acceptAll}
              onAccept={accept}
            />
          )}

          {/*
        ①' 免确认模式下的「来新活了」。
        单子已经是他的了,不需要确认 —— 但**新活不能悄悄多一行**。
        推送目前是空实现,这张卡是他唯一的信号。
      */}
          {pendingAccept.length === 0 && freshTasks.length > 0 && (
            <PendingAcceptCard
              tasks={freshTasks}
              busy={false}
              mode="notify"
              onAcceptAll={() => undefined}
              onAccept={() => undefined}
              onDismiss={dismissFresh}
            />
          )}

          {/* 取货入口:有待取货的单时出现 */}
          {c.toPickup > 0 && (
            <button
              onClick={onOpenPickup}
              className="mx-4 mt-3 w-[calc(100%-2rem)] rounded-2xl bg-blue-50 dark:bg-blue-950/40
                     border border-blue-200 dark:border-blue-900 p-4 text-left active:scale-[0.99] transition"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                    <InlineIcon icon={Store} /> 待取货 · {c.toPickup} 单
                  </div>
                  <div className="text-sm text-slate-500 mt-0.5">点击查看取货清单</div>
                </div>
                <span className="text-2xl text-slate-400">›</span>
              </div>
            </button>
          )}

          {/*
        ★ 「正在去的那一单」只出现在**配送中**这一栏。
 
          原来它顶在所有栏的最上面,而它是从一堆单里按状态优先级挑出来的 ——
          司机的原话是「不知道它存在的意义是啥」。确实:在「待取货」那一栏
          看到一张写着别的单的大卡片,只会让人迷惑。
 
          放进「配送中」之后它就有了明确的含义:**你手上这几单里,
          正在去的是这一单**。而它是怎么被选中的也变得清楚 ——
          点哪一单的导航,那一单就成为它。
      */}
          {(tab === 'DELIVERING' || tab === 'FAILED') && current && (
            <CurrentTask
              task={current}
              index={currentIndex}
              total={active.length}
              onComplete={() => setCompleting(current)}
              onChanged={load}
              onOpenDetail={onOpenOrder}
              /*
               * ★ 当前任务卡上那个 WhatsApp 也跟着同一个开关。
               *   只藏详情页里那个的话,同一个按钮在一处有、一处没有 ——
               *   人会以为是加载出了问题。
               */
              whatsapp={whatsapp}
            />
          )}

          {/*
        ★ 「已完成」单独走一条路:它是**查**东西的地方,不是今天的待办。
          从今日任务里筛的话只有今天的,而司机来这一栏问的是
          「上周三那单结了没有」—— 今天的数据答不了。
          见 CompletedList:分页往下翻,每单带结算状态。
      */}
          {tab === 'DONE' && <CompletedList onOpenOrder={onOpenOrder} />}

          {tab !== 'DONE' && listed.length === 0 && (
            <div
              className="mx-4 mt-6 rounded-2xl border border-dashed border-slate-300
                        dark:border-slate-700 p-10 text-center text-slate-400"
            >
              这一栏里没有单
              {futureCount > 0 && !showFuture && (
                <span className="mt-1 block text-xs">
                  还有 {futureCount} 单不是今天送的 —— 勾上顶栏的「非当日达」就能看到
                </span>
              )}
            </div>
          )}

          {/*
        ★ 列表**不折叠**。
 
          原来默认收起、要点一下「今日全部」才展开 —— 而司机点某个标签
          就是为了看那一栏里有哪些单。收起来的话,他点完标签看到的是
          一个写着数字的按钮,还得再点一次。
 
          折叠是为「一屏塞了太多东西」准备的;而现在这一屏就是一个列表,
          没有要折叠的理由。
      */}
          {/* ★ 「已完成」有自己的列表(分页 + 结算状态),这一块不再画它 */}
          {tab !== 'DONE' && (
            <div className="mx-4 mt-4">
              <div
                className="flex flex-wrap items-center justify-between gap-2 py-2
                        text-slate-600 dark:text-slate-400"
              >
                <span className="font-medium">
                  {/* ★ 隔夜单这一栏要自报家门 —— 点进来之后不写清楚,
                  他会以为这就是「全部」,然后奇怪今天的单怎么不见了 */}
                  {tab === 'all' ? '今日全部' : tab === 'STALE' ? '今天之前没走完的' : '这一栏'} (
                  {listed.length})
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-sm">
                    完成 {c.doneToday} · 待办 {c.toPickup + c.toDeliver}
                    {c.failed > 0 && ` · 未送达 ${c.failed}`}
                  </span>
                  {/* 发货那一段默认收起 —— 见 PickupToggle 里为什么是列表级开关 */}
                  <PickupToggle on={showPickup} onChange={setShowPickup} />
                </div>
              </div>

              {/*
          ★ 批量操作条 —— **粘在顶栏下面**,不管列表滑到哪儿,勾了就在眼前。
 
            · `top: var(--hdr-h)` 是顶栏的实际高度(App.tsx 用 ResizeObserver 写的)。
              不写死:顶栏带 pt-safe,刘海屏上比普通屏高十几个像素,
              写死之后要么压在顶栏上、要么露出一条会闪的缝。
 
            · 用 sticky 不用 fixed:sticky **占着自己那份布局空间**,
              所以滑回顶部时列表自然排在它下面,不会被挡。
              fixed 脱离文档流,第一行会被永久盖住。
              用户原话:「把数据拉到顶部的时候数据要在这个区域下面不要被挡住」。
 
            · z-20 < 顶栏的 z-30:两条都粘住时,它在顶栏**下面**,不遮住上线开关。
        */}
              {canPick && picked.size > 0 && (
                <div
                  aria-label="批量操作"
                  className="sticky top-[var(--hdr-h,3.25rem)] z-20 -mx-1 mb-2 rounded-xl border
                       border-brand-200 bg-brand-50 px-3 py-2 shadow-sm
                       dark:border-brand-800 dark:bg-brand-950"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-slate-700 dark:text-slate-200">
                      已选 {picked.size} 单
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPicked(new Set())}
                        className="h-9 rounded-lg px-3 text-sm text-slate-600 dark:text-slate-300"
                      >
                        取消
                      </button>
                      {/*
                  ★ 一单都批不了时**不摆那个按钮**。
                    摆一个灰的「送达 0 单」是在装样子:他会去点、点不动,
                    然后猜是不是自己勾错了。直接把原因写出来。
                */}
                      {batchable.length > 0 && (
                        <button
                          onClick={() => void deliverPicked()}
                          disabled={batching || !canSubmit}
                          className="h-9 rounded-lg bg-brand-500 px-3 text-sm font-medium text-white
                               disabled:opacity-40"
                        >
                          {batching ? '记着…' : `按本人签收送达 ${batchable.length} 单`}
                        </button>
                      )}
                    </div>
                  </div>
                  {/*
              ★ 批不了的那几单**点之前**就说,别让他点完在结果里看到一排失败。
                要拍照的交不出每一单的照片;要代收的,钱不能靠一次点头
                替几十单认下来(管理端那边同一个规矩)。
            */}
                  {blocked.length > 0 && (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                      {batchable.length === 0
                        ? `这 ${blocked.length} 单要拍照或金额待定,批量送达用不上 —— 得一单一单进详情点`
                        : `其中 ${blocked.length} 单要拍照或金额待定 —— 这几单得一单一单进详情点送达`}
                    </p>
                  )}

                  {/*
              ★ 特殊物品和现金**各问一次**,不逐单问。
                逐单问等于让他在批量里做十次单条操作,那他不如直接进详情;
                一次不问就是替他认下东西和钱 —— 而后端也会逐单拒掉。
            */}
                  {needsHandover && (
                    <label
                      aria-label="确认特殊物品已交付"
                      className="mt-2 flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4"
                        checked={handoverOk}
                        onChange={(e) => setHandoverOk(e.target.checked)}
                      />
                      <span>
                        这 <b>{specialPicked.length} 单带特殊物品</b>
                        {specialTally.length > 0 && `(${specialTally.join('、')})`} ——
                        都已经交到收件人 手上了
                      </span>
                    </label>
                  )}
                  {needsCash && (
                    <label
                      aria-label="确认代收现金已收齐"
                      className="mt-2 flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4"
                        checked={cashOk}
                        onChange={(e) => setCashOk(e.target.checked)}
                      />
                      {/*
                  ★ 把**合计**摆出来,不只说「有几单要收钱」。
                    他要做的动作是低头数一遍手上的现金,那一步需要的是一个数字;
                    没有这个数,这个勾就只是个形式。
                */}
                      <span>
                        这 <b>{cashPicked.length} 单要收现金</b>,合计{' '}
                        <b className="tabular-nums">
                          {money(String(cashTotal), cashPicked[0]?.currency ?? '')}
                        </b>{' '}
                        —— 都收齐了
                      </span>
                    </label>
                  )}
                </div>
              )}

              {listed.length > 0 && (
                /*
            ★ 给这个列表起个名字。屏幕上不止一处会出现单号
            (顶部的待接单卡片也会),用例如果满屏找文本,
            断言的其实是「屏幕上有没有」而不是「这一栏里有没有」。
          */
                <ul className="space-y-1.5" aria-label="任务列表">
                  {shown.map((t, i) => {
                    const done = ['DELIVERED', 'RETURNED', 'CANCELLED'].includes(t.status);
                    const failed = t.status === 'FAILED';
                    return (
                      <li key={t.id} className="flex items-stretch gap-1.5">
                        {/*
                    ★ 复选框在最左边,而且**只在「配送中」这一栏出现**。
                      放在整行按钮外面:点行是进详情,点框是勾选 ——
                      套在一起的话,他想勾一单结果被弹进详情页。
                  */}
                        {canPick && !done && (
                          <label className="flex shrink-0 items-center px-1">
                            <input
                              type="checkbox"
                              aria-label={`选择 ${t.orderNo}`}
                              checked={picked.has(t.id)}
                              onChange={() => togglePick(t.id)}
                              className="h-5 w-5"
                            />
                          </label>
                        )}
                        <button
                          /*
                           * ★ 点一单进**详情**,而不是「设为当前任务」。
                           *
                           *   原来点一下只是把它顶到上面那张卡片上 ——
                           *   而司机点一单想做的是「对这一单干点什么」:
                           *   取货、送达、打个电话。那些操作原来散在别的页面里
                           *   (取货要绕到「待取货」二级页),从列表根本走不到。
                           *
                           * ★ 但「设为当前」不能顺手删掉 —— 那是他开车时用的:
                           *   排顺序、以及把未送达的那几单捞回当前卡片。
                           *   所以拆成两个**各自说得清**的入口:整行进详情,
                           *   右边那个 ▶ 设为当前。一个动作两种含义才是原来的问题。
                           */
                          onClick={() => onOpenOrder(t.id)}
                          aria-label={`打开订单详情:${t.receiverName} ${t.orderNo}`}
                          aria-current={t.id === current?.id ? 'true' : undefined}
                          className={`flex-1 min-w-0 text-left flex items-center gap-3 rounded-xl px-3 py-2.5 border
                    ${done ? 'opacity-50 border-slate-200 dark:border-slate-800' : ''}
                    ${failed ? 'border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-950/20' : ''}
                    ${!done && !failed ? 'border-slate-200 dark:border-slate-800' : ''}
                    ${t.id === current?.id ? 'ring-2 ring-brand-500' : ''}`}
                        >
                          <span className="text-slate-400 tabular-nums text-sm w-5">{i + 1}</span>
                          <span className="shrink-0">
                            {done ? (t.status === 'DELIVERED' ? '✓' : '—') : failed ? <InlineIcon icon={X} /> : '▶'}
                          </span>
                          <div className="min-w-0 flex-1">
                            {/*
                      ★ 和「可领取」用同一份卡片。
                        用户原话:「其它标签格式中的列表显示方式也应该和可领取一样吧,
                        现在只显示名字和订单号」—— 只给名字和单号的话,
                        司机在这一屏排今天的顺序时判断不了哪几单顺路,
                        他得点进每一单看。
                    */}
                            <TaskCardBody
                              showPickup={showPickup}
                              data={{
                                receiverName: t.receiverName,
                                receiverAddress: t.receiverAddress,
                                // 取货之后地址被改过的话,卡片上要标出来(见 TaskCardBody)
                                addressChangedAt: t.addressChangedAt ?? null,
                                pickupName: t.pickup?.name ?? t.pickupPoint?.name,
                                pickupAddress: t.pickup?.address ?? t.pickupAddress,
                                zone: t.zone?.name,
                                packageCount: t.packageCount,
                                weightGram: t.weightGram,
                                // ★ 代收在下面那一行已经有醒目标记,这里不重复显示金额
                                codAmount: 0,
                                // 智能推荐路线的序号和预计到达 —— 没规划过时是 null,卡片上不显示
                                routeSeq: t.routeSeq ?? null,
                                routeEtaAt: t.routeEtaAt ?? null,
                              }}
                            />
                            <div className="mt-1 flex items-center gap-1.5 truncate text-xs text-slate-500">
                              {t.orderNo}{' '}
                            </div>
                          </div>
                          <span className="text-xs text-slate-500 shrink-0 flex items-center gap-1">
                            {pendingIds.has(t.id) && (
                              <span title="待同步" className="text-blue-500">
                                <InlineIcon icon={Hourglass} />
                              </span>
                            )}
                            {STATUS_LABEL[t.status]}
                            {t.subStatus && ` · ${t.subStatus}`}
                          </span>
                        </button>

                        {/* 设为当前任务 —— 开车时用的那条路径 */}
                        {!done && (
                          <button
                            onClick={() => setPinnedId(t.id)}
                            aria-label={`选为当前任务:${t.receiverName} ${t.orderNo}`}
                            className={`w-11 shrink-0 rounded-xl border text-slate-400
                        ${
                          t.id === current?.id
                            ? 'border-brand-500 text-brand-600 dark:text-brand-500'
                            : 'border-slate-200 dark:border-slate-800'
                        }`}
                          >
                            ▶
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {/*
          ★ 滑到底自动接着画,直到这一栏全部显示完。
            「还有 N 单没列出来」那种提示对司机没有用:
            他要的是把自己的单看完,而不是被告知看不完。
        */}
              <div ref={bottomCallbackRef} className="py-3 text-center text-xs text-slate-400">
                {hidden > 0 ? `正在往下加载(还有 ${hidden} 单)` : shown.length > 0 ? '到底了' : ''}
              </div>
            </div>
          )}
        </>
      )}

      {completing && (
        <CompleteSheet
          task={completing}
          deliverOnly={deliverOnly}
          confirmPrechecked={confirmPrechecked}
          onClose={() => setCompleting(null)}
          onDone={async () => {
            setCompleting(null);
            await load();
          }}
        />
      )}
    </div>
  );
}
